import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AboutUs.css';

function AboutUs() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "About Us | Intellient Solutions";
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('abt-visible');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    document.querySelectorAll('.abt-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const coreValues = [
    {
      num: "01",
      title: "Client-Promise First",
      description: "We don't just deliver projects — we own outcomes. Rooted in the Saudi spirit of amanah (trust and responsibility), we wake up every day asking: how does this move our client forward?",
    },
    {
      num: "02",
      title: "Innovation with Purpose",
      description: "We think beyond the next quick fix. Inspired by the Kingdom's bold transformation journey, every idea, every line of code, and every architecture we build is designed to spark meaningful progress — for Saudi businesses and the communities they serve.",
    },
    {
      num: "03",
      title: "Excellence & Ownership",
      description: "We take ownership, not just tasks. Excellence is not optional; it is the standard set by our nation's pursuit of global leadership. We hold ourselves to the highest benchmarks in technology, service, and every decision we make.",
    },
    {
      num: "04",
      title: "National Stewardship",
      description: "We are proud to be Saudi, and we build bridges from the Kingdom to the world. By nurturing local talent and forging international partnerships, we advance the Vision 2030 goal of positioning Saudi Arabia as a global hub for innovation.",
    },
    {
      num: "05",
      title: "Diverse Collaboration",
      description: "We are stronger together. Drawing on the rich heritage of the Kingdom and the energy of its youth, we bring together diverse backgrounds, perspectives, and disciplines — because culture, technology, and ideas thrive when shared.",
    },
    {
      num: "06",
      title: "Trusted Resilience",
      description: "Trust is earned; resilience is built. We secure our systems, safeguard privacy in line with Saudi data protection regulations (PDPL and NCA frameworks), and design for durability — so our clients can rely on us, always.",
    }
  ];

  return (
    <>
      {/* ── PAGE HERO ── */}
      <section className="abt-page-hero">
        <div className="abt-container">
          <div className="abt-breadcrumb">
            <a href="/">Home</a>
            <span className="abt-breadcrumb-sep">/</span>
            <span className="abt-breadcrumb-current">About</span>
          </div>
          <div className="abt-page-hero-grid">
            <div>
              <div className="abt-section-eyebrow abt-reveal abt-d1">Our Story</div>
              <h1 className="abt-reveal abt-d2">A partner built for complex ambitions.</h1>
              <p className="abt-page-hero-sub abt-reveal abt-d3">
                Headquartered in Riyadh and engaged across the region, Intellient Solutions helps enterprises and institutions turn data and AI into measurable impact — combining deep local fluency with international standards of delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STORY EDITORIAL ── */}
      <section className="abt-story-block">
        <div className="abt-container">
          <div className="abt-story-block-grid">
            <div>
              <div className="abt-section-eyebrow">Why We Exist</div>
              <h2 className="abt-section-title">A different kind of <em>technology</em> partnership.</h2>
            </div>
            <div className="abt-story-block-text">
              <p>Intellient Solutions was founded in 2022, KSA with a clear conviction: that great technology partnerships are built on understanding — of the business, the people, the context, and the outcomes that matter. Too many engagements deliver software but miss the point.</p>
              <p>We design governed data platforms, scale AI responsibly, and engineer secure systems that earn trust on day one and continue earning it on day one thousand. We work with banks, public-sector entities, multinationals, and ambitious enterprises — every engagement starts with listening and ends with capability that lasts.</p>
              <p>What sets us apart is not what we build, but how we build it. We combine the engineering rigor international clients require with the local fluency that organizations operating in Saudi Arabia depend on. The result is a partner who can hold both worlds at once.</p>
              <div className="abt-story-pullquote">
                <p>We measure success not in hours billed, but in the capability and clarity we leave behind.</p>
                <div className="abt-story-pullquote-attr">— The Intellient Way</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CORE VALUES ── */}
      <section className="abt-values">
        <div className="abt-container">
          <div className="abt-section-header abt-reveal">
            <div className="abt-section-eyebrow">A different kind of technology partnership</div>
            <h2 className="abt-section-title">Six values, one philosophy.</h2>
            <p className="abt-section-intro">These aren't aspirations — they're the standards we use to make hard decisions, hire teammates, and earn our clients' trust.</p>
          </div>
          <div className="abt-values-grid">
            {coreValues.map((value, idx) => (
              <div key={idx} className={`abt-value-card abt-reveal abt-d${(idx % 3) + 1}`}>
                <div className="abt-value-num">{value.num}</div>
                <h3 className="abt-value-title">{value.title}</h3>
                <p className="abt-value-desc">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── CERTIFICATIONS ── */}
      <section className="abt-certs">
        <div className="abt-container">
          <div className="abt-section-header abt-reveal">
            <div className="abt-section-eyebrow">Standards & Certifications</div>
            <h2 className="abt-section-title">Trust, verified.</h2>
            <p className="abt-section-intro">Our delivery meets the regulatory and security standards required by government, banking, and enterprise clients — both local and multinational.</p>
          </div>
          <div className="abt-certs-grid">
            <div className="abt-cert-card abt-reveal abt-d1">
              <div className="abt-cert-icon">NCA</div>
              <div className="abt-cert-name">NCA-ECC Aligned</div>
              <div className="abt-cert-full">National Cybersecurity Authority</div>
              <div className="abt-cert-desc">Essential Cybersecurity Controls compliance — the baseline standard for systems serving Saudi government and critical-infrastructure sectors.</div>
            </div>
            <div className="abt-cert-card abt-reveal abt-d2">
              <div className="abt-cert-icon">PDPL</div>
              <div className="abt-cert-name">PDPL Compliant</div>
              <div className="abt-cert-full">Personal Data Protection Law</div>
              <div className="abt-cert-desc">Full compliance with the Kingdom's Personal Data Protection Law — protecting personal and sensitive data across every engagement.</div>
            </div>
            <div className="abt-cert-card abt-reveal abt-d3">
              <div className="abt-cert-icon">SAMA</div>
              <div className="abt-cert-name">SAMA Aligned</div>
              <div className="abt-cert-full">Saudi Central Bank Frameworks</div>
              <div className="abt-cert-desc">Delivery aligned with SAMA cyber security and IT governance frameworks for financial services and banking clients.</div>
            </div>
            <div className="abt-cert-card abt-reveal abt-d1">
              <div className="abt-cert-icon">SDAIA</div>
              <div className="abt-cert-name">SDAIA Aligned</div>
              <div className="abt-cert-full">Data & AI Authority Standards</div>
              <div className="abt-cert-desc">AI and data engagements delivered in line with SDAIA's national frameworks for responsible data and AI practice.</div>
            </div>
            <div className="abt-cert-card abt-reveal abt-d2">
              <div className="abt-cert-icon">ISO</div>
              <div className="abt-cert-name">ISO 27001</div>
              <div className="abt-cert-full">Information Security Management</div>
              <div className="abt-cert-desc">International standard for information security management — covering our own operations and the systems we deliver.</div>
            </div>
            <div className="abt-cert-card abt-reveal abt-d3">
              <div className="abt-cert-icon">ISO</div>
              <div className="abt-cert-name">ISO 9001</div>
              <div className="abt-cert-full">Quality Management Systems</div>
              <div className="abt-cert-desc">International standard for quality management — ensuring consistent delivery, documentation, and process discipline on every engagement.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="abt-cta">
        <div className="abt-container">
          <div className="abt-cta-inner">
            <div className="abt-cta-content">
              <div className="abt-cta-eyebrow">Start a Conversation</div>
              <h2>Let's build something that lasts.</h2>
              <p>Every great engagement begins with a conversation. Tell us about your ambition — we'll tell you how we can help.</p>
              <button onClick={() => navigate("/contact")} type="submit" className="abt-btn-cta-primary">Request a Meeting <span>→</span></button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default AboutUs;
